import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Who is signed in to the UI.
 *
 * Deliberately separate from RoleContext: that one picks a demo *persona*
 * (Process Engineer, EHS Lead, ...) to frame what the dashboards emphasise, and
 * is a local preference. This one is identity — it decides whether the app
 * renders at all, and whether write controls are offered.
 *
 * Scope: this is a UI gate. `canWrite` hides controls; it does not protect the
 * API, which accepts unauthenticated calls by design. See backend/auth.py.
 */
const AuthContext = createContext(null);

// "checking" until /auth/me answers. Rendering the login screen during that
// window would flash it in front of an already-signed-in user on every reload.
const CHECKING = "checking";

export function AuthProvider({ children }) {
  const [status, setStatus] = useState(CHECKING);
  const [user, setUser] = useState(null);
  const [authEnabled, setAuthEnabled] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser({ username: data.username, role: data.role });
      setAuthEnabled(Boolean(data.auth_enabled));
      setStatus("authenticated");
    } catch (err) {
      if (err?.response?.status === 401) {
        setUser(null);
        setStatus("anonymous");
        // 401 only happens when the server has auth on.
        setAuthEnabled(true);
        return;
      }
      // Backend unreachable or erroring: treat as anonymous so the user gets the
      // login screen with an explanation, rather than a blank app.
      setUser(null);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const login = useCallback(async (username, password) => {
    try {
      const { data } = await api.post("/auth/login", { username, password });
      setUser({ username: data.username, role: data.role });
      setAuthEnabled(true);
      setStatus("authenticated");
      return { ok: true };
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401 || status === 429 || status === 503) {
        return { ok: false, error: formatApiErrorDetail(detail) };
      }
      if (!err?.response) {
        return { ok: false, error: "Cannot reach the server. Is the backend running?" };
      }
      return { ok: false, error: formatApiErrorDetail(detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      // Clear locally even if the request failed — the user asked to be signed
      // out, so the UI must stop showing their session.
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  const role = user?.role || null;

  const value = {
    status,
    checking: status === CHECKING,
    isAuthenticated: status === "authenticated",
    user,
    role,
    authEnabled,
    // Viewers get a read-only UI. When auth is switched off entirely the backend
    // reports role "admin", so nothing is hidden on an unsecured deployment.
    canWrite: role !== "viewer",
    isViewer: role === "viewer",
    login,
    logout,
    reload: load,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext) || {};
}
